import _ from 'lodash'
import DrawerLayout from 'scoreboard/components/drawer-layout'
import DatasetFilters from 'scoreboard/datasets/dataset-filters'
import Dataset from 'scoreboard/datasets/model/dataset'
import spotlight from 'scoreboard/components/spotlight'
import permissions from 'scoreboard/components/permissions'
import isNoop from 'scoreboard/datasets/filters/is-noop'
import i18n from 'scoreboard/components/i18n'
import constants from 'scoreboard/components/constants'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import layoutManager from 'scoreboard/components/layout-manager'
import mobile from '../../components/mobile'

const DatasetFiltersLayout = DrawerLayout.extend({
	className: DrawerLayout.prototype.className + ' display-layout-dashboards standard-layout',

	/**
	 * Implementation of drawer-layout for displaying dataset filters on a dashboards
	 *
	 * @param {Object} opt
	 *
	 * @param {Maestro} opt.maestro instance of Maestro used by the dashboard
	 *
	 * @param {Boolean} opt.allowAdd - whether the Add button will be displayed
	 */
	initialize: function (opt = {}) {
		DrawerLayout.prototype.initialize.apply(this, arguments)

		this.options = opt

		// #36008 listen for query:skipped events, in case the last action was
		// to delete the last widgets for a given dataset. It won't result in
		// a query to the server, but we still need to update the drawer
		this.listenTo(this.options.maestro, 'query:done query:skipped chartFetch:done',
			() => this.renderFilters())

		this.firstRender = true
	},

	/**
	 * @override
	 */
	renderDrawerContent: function (container) {
		// Currently nothing to do here; we need to wait for canvas to finish
		// rendering before we can do anything
	},

	/**
	 * @override
	 * If the .dashboard-info section is visible, we need to take that into account
	 * when figuring out what the max drawer height is.
	 */
	_getMaxDrawerHeight() {
		let max = DrawerLayout.prototype._getMaxDrawerHeight.apply(this, arguments)
		let info = this.$('.dashboard-info')
		if (info.length > 0) {
			let adjustedMax = Math.min(max, this.$('.display-container').height() - info.height())
			// Since there's limited space here in the dashboards edit screen with the dashboard-info
			// section present, and since there can be multiple layouts to the screen, we need to first figure out
			// what layout we're in, and then adjust according to the space that layout affords us for this drawer
			// to appear in.

			// TODO: Remove when V5 is no longer supported (dashboard edit fullscreen stuff)
			let fullscreen = layoutManager.inLayout(constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN)
			if (mobile.isMobileOrientation()) {
				let mobileMax = 0
				if (fullscreen) {
					mobileMax = this.$el.height() - constants.DASHBOARD_MOBILE_TOOLBAR_HEIGHT*2
				} else {
					mobileMax = this.$el.height() - this.$('header').height() - this.$('.dashboard-data').height()
				}

				return mobileMax * this.DRAWER_HEIGHT_ADJUSTMENT
			} else {
				// If the calculated drawer height ends up lower then 600px, then just make
				// it take up the whole .display-pane-body.
				if (adjustedMax < 600 && !fullscreen) {
					return this.$('.display-pane-body').height() + constants.DRAWER_CLOSED_HEIGHT
				} else {
					return adjustedMax
				}
			}
		} else {
			return max
		}
	},

	/**
	 * Updates the drawer content with the current filters on the dashboard
	 */
	renderFilters: function () {
		let datasets = this.options.maestro.datasets()

		let filtersByDatasetId = this.currentFilters(datasets)
		let treeItemsById = _.extend(this.options.maestro.data.datasetTreeItemsById(),
			this.options.maestro.chartTreeItemsById())
		let showDatasetLabel = datasets.length > 1

		this.removeFiltersViews()

		this.filtersViews = datasets.map(dataset => {
			let filters = (filtersByDatasetId[dataset.id] || [])
				.filter(f => !isNoop(f, dataset))

			let view = new DatasetFilters({
				model: dataset,
				filters,
				treeItemsById,
				allowAdd: this.options.allowAdd,
				showDatasetLabel
			})

			view.$el.addClass('drawer-item')

			this.listenTo(view, 'change', (allFilters, opt) => {
				if (!opt.newFilter) {
					// This was a delete; let the 'delete-filter' listener below
					// take care of it
					return
				}
				this.changeDrawerFilters(dataset.id, allFilters)
			})

			this.listenTo(view, 'delete-filter', deleted => {
				if (deleted.filterWidget) {
					// #26934 If a filter widget's filter is removed from the drawer,
					// set the filter widget to a default "empty" state. This will
					// make it disappear from the drawer (#26925)
					clearFilterState(deleted, dataset)

					// Add the deleted (and now "empty") filter back into the
					// drawer's list of filters
					this.changeDrawerFilters(
						dataset.id,
						view.filters.concat(deleted))
				} else {
					// If it wasn't a filter widget filter, then just delete it
					this.options.maestro.deleteFilter(dataset.id, deleted)
				}
			})

			return view
		})

		this.filtersViews.forEach(v => {
			this.$('.drawer-content').append(v.el)
			v.render()
		})

		let oldHandleText = this.getHandleText()

		this.resize()

		// If the filter update changes the handle text (ie changes the number
		// of filters), spotlight the handle
		if (!this.firstRender && oldHandleText !== this.getHandleText()) {
			spotlight(this.$('.handle'))
		}

		this.firstRender = false
	},

	/**
	 * @override
	 */
	_updateHandleText() {
		if (this._isOpen()) {
			this._setHandleText(i18n.get('common.close'))
		} else {
			this._setHandleText(i18n.get('dashboards.datasets.showFilters', this.count()))
		}
	},

	getHandleText: function () {
		return this.$('.handle .text').text()
	},

	/**
	 * Returns all current filters applied to the dashboard, including those from
	 * filter widgets, drilldowns, and permissions
	 *
	 * @param datasetModels Dataset model instances used in the dashboard; used
	 * to retrieve permission filters
	 *
	 * @return {Object} all active filters; key is dataset ID, value is an array
	 * of filters for that dataset
	 */
	currentFilters: function (datasetModels) {
		let liveFilters = this.options.maestro.liveFilters()

		return _(datasetModels)
			.map(dataset => {
				let filters = liveFilters[dataset.id] || []

				if (!permissions.modifyAllDatasets()) {
					// #26218 if the user isn't in an "Admin All Datasets" group,
					// look for dataset permission filters than match the user
					let userPermissionFilters = _(dataset.get('permissions'))
						.filter(p => p.matchesUser)
						.flatMap(p => p.filters || [])
						.value()

					if (!dataset.get('hasUnfilteredPermission')) {
						// #26218 only include permission filters when none of them
						// give unfiltered permission (an unfiltered permission gives
						// full access and cancels out other permission filters)
						filters = filters.concat(userPermissionFilters)
					}
				}

				return [ dataset.id, filters ]
			})
			.fromPairs()
			.value()
	},

	/**
	 * Callback for filter updates done through the drawer controls (as opposed
	 * to interacting with the dashboard widgets)
	 *
	 * @param datasetId
	 *
	 * @param allFilters array of all filters on the given dataset ID
	 */
	changeDrawerFilters: function (datasetId, allFilters) {
		let [ drawerFilters, widgetFilters] = _.partition(allFilters, f => {
			// When deciding which filters were added directly through the drawer,
			// ignore the following:
			//  - Permission filters (f.permissionId)
			//  - dataset-filter widget filters (f.filterWidget)
			//  - Table drilldown filters (f.drilldownId && f.drilldownActionId)
			//
			// Note: don't need to worry about any manually added filters on dataset-value
			// or dataset-table filters, because they don't show up in the drawer
			//
			// Everything left is a filter that was manually added through the drawer UI
			return !f.permissionId && !f.filterWidget && !f.drilldownId && !f.drilldownActionId
		})

		drawerFilters
			.filter(f => !f.cid)
			.forEach(f => {
				// Give each drawer filter a cid so we can keep track of them
				// when making changes
				f.cid = 'f' + nextFilterCid++
				f.drawerFilter = true
			})

		this.options.maestro.changeFilters({ datasetId, drawerFilters, widgetFilters })
	},

	/**
	 * @override
	 */
	count: function () {
		let datasets = this.options.maestro.datasets()
		let datasetsById = _.keyBy(datasets, 'id')
		return _(this.currentFilters(datasets))
			.toPairs()
			.flatMap(([datasetId, filters]) => {
				let dataset = datasetsById[datasetId]
				return filters.filter(f => !isNoop(f, dataset))
			})
			.value()
			.length
	},

	/**
	 * @override
	 */
	_shouldDrawerStayOpen() {
		return false
	},

	removeFiltersViews: function () {
		_(this.filtersViews).forEach(v => v.remove())
	},

	remove: function () {
		DrawerLayout.prototype.remove.apply(this, arguments)
		this.removeFiltersViews()
	}
})

let nextFilterCid = 1

/**
 * Deletes the given list of properties from the given object
 */
function clearProps (object, props) {
	props.forEach(p => delete object[p])
}

/**
 * Modifies the given filter to an empty/default state that won't affect
 * the dataset query
 */
function clearFilterState (filter, dataset) {
	let field = dataset.getUnderlyingField(filter.fieldId)

	let dateOrPeriodType = isDateTimeDataType(field.dataType) || field.dataType === 'calendar-period'

	if (filter.operator === 'is-blank' || filter.operator === 'not-blank') {
		// #27021 we generally want to leave the operator alone and just blank
		// out the values, but these operators don't have any values/state to
		// blank out. Instead, we have to switch to 'is-any' (or is-any-period
		// for date fields, because is-any doesn't exist for them)
		filter.operator = dateOrPeriodType ? 'in-any-period' : 'is-any'
	}

	if (dateOrPeriodType) {
		if (_.includes(['before', 'after', 'between-date-time', 'in-period'], filter.operator)) {
			filter.calendarId = Dataset.ALL_TIME
		}

		clearProps(filter, [
			'periodId1', 'periodId2', 'periodDelta1', 'periodDelta2',
			'date1', 'date2', 'periodIds', 'dates', 'days', 'months'
		])
	} else {
		clearProps(filter, [
			'values', 'value1', 'value2',
			'equations', 'equation1', 'equation2',
			'rangeIds', 'trees', 'updateType1', 'updateType2',
			'matching', 'anotherTreeFieldId', 'rollupIsAnyFilters',
			'times', 'time1', 'time2'
		])
	}
}

export default DatasetFiltersLayout
export { clearFilterState }
DatasetFiltersLayout.__filename__ = 'dashboards/display/dataset-filters-layout.js'
